@layer refine.base {
  :host {
    display: flex;
    gap: var(--size-1);
    align-items: center;
  }

  .pagination {
    display: flex;
    gap: var(--pagination-gap, var(--size-1));
    align-items: center;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  li {
    display: contents;
  }

  button {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--pagination-item-size, var(--size-9));
    height: var(--pagination-item-size, var(--size-9));
    min-height: var(--_touch-target);
    padding: 0 var(--size-2);
    font-size: var(--_font-size, var(--text-sm));
    font-weight: var(--font-medium);
    color: var(--color-contrast-700);
    cursor: pointer;
    user-select: none;
    border: var(--border) solid transparent;
    border-radius: var(--pagination-radius, var(--rounded-lg));
    transition:
      background var(--transition-fast),
      color var(--transition-fast),
      border-color var(--transition-fast);
  }

  /* Default (ghost-like) behavior unless variant is specified */
  :host(:not([variant])) button,
  :host([variant='ghost']) button {
    color: var(--color-contrast-700);
    background: transparent;
    border-color: transparent;
    border-width: 0;
  }

  :host(:not([variant])) button:hover:not(:disabled, [aria-current='page']),
  :host([variant='ghost']) button:hover:not(:disabled, [aria-current='page']) {
    background: var(--_theme-backdrop);
  }

  :host([variant='solid']) button {
    color: var(--_theme-contrast);
    background: var(--_theme-base);
    border-color: var(--_theme-base);
  }

  :host([variant='solid']) button:hover:not(:disabled, [aria-current='page']) {
    background: var(--_theme-focus);
  }

  :host([variant='flat']) button {
    color: var(--_theme-base);
    background: color-mix(in oklch, var(--_theme-backdrop) 8%, var(--color-contrast-100));
    border-color: color-mix(in oklch, var(--_theme-focus) 20%, transparent);
    box-shadow: var(--inset-shadow-2xs);
  }

  :host([variant='flat']) button:hover:not(:disabled, [aria-current='page']) {
    color: var(--_theme-contrast);
    background: var(--_theme-focus);
  }

  :host([variant='bordered']) button {
    color: var(--_theme-base);
    background: var(--_theme-backdrop);
    border-color: var(--_theme-border);
    box-shadow: var(--inset-shadow-xs);
  }

  :host([variant='bordered']) button:hover:not(:disabled, [aria-current='page']) {
    color: var(--_theme-contrast);
    background: var(--_theme-focus);
    border-color: var(--_theme-focus);
  }

  :host([variant='outline']) button {
    color: var(--_theme-base);
    background: transparent;
    border-color: var(--_theme-base);
  }

  :host([variant='outline']) button:hover:not(:disabled, [aria-current='page']) {
    background: var(--_theme-backdrop);
    border-color: var(--_theme-focus);
  }

  :host([variant='text']) button {
    color: var(--_theme-base);
    background: transparent;
    border-color: transparent;
    border-width: 0;
  }

  :host([variant='text']) button:hover:not(:disabled, [aria-current='page']) {
    color: var(--_theme-focus);
    background: transparent;
  }

  :host([variant='frost']) button {
    color: var(--_theme-content);
    background: color-mix(in oklch, var(--_theme-backdrop) 45%, transparent);
    border-color: color-mix(in oklch, var(--_theme-border) 45%, transparent);
    backdrop-filter: blur(var(--blur-xl)) saturate(200%);
  }

  :host([variant='frost']) button:hover:not(:disabled, [aria-current='page']) {
    background: color-mix(in oklch, var(--_theme-backdrop) 65%, transparent);
    border-color: color-mix(in oklch, var(--_theme-focus) 55%, transparent);
  }

  :host([variant='glass']) button {
    color: var(--_theme-content);
    background: color-mix(in oklch, var(--color-canvas) 70%, transparent);
    border-color: color-mix(in oklch, var(--_theme-border) 40%, transparent);
    backdrop-filter: blur(var(--blur-md)) saturate(140%);
  }

  :host([variant='glass']) button:hover:not(:disabled, [aria-current='page']) {
    background: color-mix(in oklch, var(--color-canvas) 55%, transparent);
    border-color: color-mix(in oklch, var(--_theme-border) 45%, transparent);
  }

  button:hover:not(:disabled, [aria-current='page']) {
    background: var(--color-contrast-100);
  }

  button:focus-visible {
    outline: var(--border-2) solid var(--_theme-base, var(--color-contrast-500));
    outline-offset: var(--border-2);
  }

  /* Selected page styling - applies to all variants */
  button[aria-current='page'] {
    font-weight: var(--font-bold);
    color: var(--_theme-contrast, var(--color-canvas));
    cursor: default;
    background: var(--_theme-base, var(--color-contrast-800));
    border-color: var(--_theme-base, var(--color-contrast-800));
  }

  :host([variant='ghost']) button[aria-current='page'],
  :host(:not([variant])) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: var(--_theme-focus);
    border-color: var(--_theme-focus);
  }

  :host([variant='solid']) button[aria-current='page'] {
    background: var(--_theme-focus);
    border-color: var(--_theme-focus);
  }

  :host([variant='flat']) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: var(--_theme-base);
    border-color: var(--_theme-base);
  }

  :host([variant='bordered']) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: var(--_theme-focus);
    border-color: var(--_theme-focus);
  }

  :host([variant='outline']) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: var(--_theme-base);
    border-color: var(--_theme-base);
  }

  :host([variant='text']) button[aria-current='page'] {
    color: var(--_theme-focus);
    text-decoration-line: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
  }

  :host([variant='frost']) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: color-mix(in oklch, var(--_theme-base) 70%, transparent);
    border-color: var(--_theme-base);
  }

  :host([variant='glass']) button[aria-current='page'] {
    color: var(--_theme-contrast);
    background: color-mix(in oklch, var(--color-canvas) 90%, transparent);
    border-color: var(--_theme-base);
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  .ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--pagination-item-size, var(--size-9));
    height: var(--pagination-item-size, var(--size-9));
    font-size: var(--_font-size, var(--text-sm));
    color: var(--color-contrast-400);
    cursor: default;
  }
}

@layer refine.utilities {
  :host([size='sm']) {
    --pagination-item-size: var(--size-7);
    --pagination-gap: var(--size-0_5);
    --_font-size: var(--text-xs);
  }

  :host([size='lg']) {
    --pagination-item-size: var(--size-11);
    --_font-size: var(--text-base);
  }
}
